import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useDeleteVoiceWebhook, useVoiceWebhookList } from '../api';
import { JsonViewerDialog } from '../components/JsonViewerDialog';
import type { VoiceWebhookRow } from '../types';

interface JsonModal {
  title: string;
  content: string;
}

export function VoiceWebhookListPage() {
  const navigate = useNavigate();
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const query = useVoiceWebhookList();
  const del = useDeleteVoiceWebhook();
  const rows = query.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<VoiceWebhookRow | null>(null);
  const [jsonModal, setJsonModal] = useState<JsonModal | null>(null);

  const hasJsonPayload = (raw: string | undefined) => {
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
    } catch {
      return false;
    }
  };

  const columns = useMemo<ColumnDef<VoiceWebhookRow>[]>(() => {
    const cols: ColumnDef<VoiceWebhookRow>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'webhookName',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/update-webhooks/${row.original.id}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.webhookName}
          </Link>
        ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => (
          <span className="break-all font-mono text-xs">{row.original.url}</span>
        ),
      },
      { accessorKey: 'event', header: 'Event', size: 180 },
      {
        id: 'headerView',
        header: 'Headers',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          hasJsonPayload(row.original.headerJson) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setJsonModal({ title: 'Headers', content: row.original.headerJson ?? '' })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'bodyView',
        header: 'Body',
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          hasJsonPayload(row.original.bodyJson) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setJsonModal({ title: 'Body', content: row.original.bodyJson ?? '' })
              }
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ];

    if (!isSubOrSuper) {
      cols.push({
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{row.original.webhookName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/update-webhooks/${row.original.id}`)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      });
    }

    return cols;
  }, [isSubOrSuper, navigate]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Webhook deleted');
        setPendingDelete(null);
      },
      onError: () => toast.error('Could not delete the webhook.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="Voice Webhooks"
        description="HTTP callbacks fired on voice campaign lifecycle events."
        actions={
          !isSubOrSuper && (
            <Button asChild>
              <Link to="/add-webhooks">
                <Plus className="h-4 w-4" />
                Add webhook
              </Link>
            </Button>
          )
        }
      />

      <DataTable<VoiceWebhookRow, unknown>
        tableId="webhooks:voice:list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search webhooks…"
        emptyTitle="No webhooks yet"
        emptyDescription="Add a webhook to receive HTTP callbacks for voice events."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={!isSubOrSuper ? { right: ['actions'] } : undefined}
        exportFilename="voice-webhooks.csv"
        pageSize={10}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            Webhook{' '}
            <span className="font-medium text-foreground">{pendingDelete?.webhookName}</span> will
            be permanently deleted. Events firing this webhook will stop being delivered.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={confirmDelete}
      />

      <JsonViewerDialog
        open={jsonModal !== null}
        onOpenChange={(o) => !o && setJsonModal(null)}
        title={jsonModal?.title ?? ''}
        content={jsonModal?.content ?? ''}
      />
    </FillPage>
  );
}
